import type { Id } from "@repo/convex/dataModel";
import { ChevronDown, ChevronRight, FolderOpen, Plus } from "lucide-react";
import { type ReactNode, type Ref, useCallback, useMemo } from "react";
import { Collapsible, CollapsibleContent } from "@/components/ui/collapsible";
import { Input } from "@/components/ui/input";
import { LongLabelTooltip } from "@/components/ui/long-label-tooltip";
import { useSidebar } from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";
import { getTreeLocationKey } from "../../data/tree-navigation-state";
import { useCreateEntityFromAction, useTreeNodeCreateActions } from "../../hooks";
import { runGuardedCreateAction } from "../../lib/create-actions";
import { useProjectNavigate } from "../../routing/project-route-context";
import type { TreeExpandState, TreeItem } from "../../types";
import { CreateEntityButton } from "../create-entity-button";
import { getFontWeight, getIconColor, hasActiveDescendant } from "./tree-helpers";

interface TreeNodeProps {
  item: TreeItem;
  level?: number;
  projectId: Id<"projects">;
  defaultOpen?: boolean;
  treeExpandState: TreeExpandState;
  projectTreeFolderDraftParentId?: string | null;
  projectTreeFolderDraftName: string;
  isSavingProjectTreeFolder: boolean;
  onProjectTreeFolderDraftChange: (value: string) => void;
  onProjectTreeFolderDraftSubmit: () => void;
  onProjectTreeFolderDraftCancel: () => void;
  onStartProjectTreeFolderDraft: (item: TreeItem) => void;
  /**
   * Content pinned to the right edge of *this* row (LAT-990: the Claudia row's needs-you dot and
   * chevron). Deliberately not forwarded to children — it belongs to one row, and a folder or
   * element row that inherited a badge from its parent would be lying about itself.
   */
  trailing?: ReactNode;
  /**
   * Replaces the row's navigate-on-activate. A row that opens a flyout instead of going
   * somewhere still keeps its route-active styling, which is driven by the location, not by the
   * click that used to set it.
   */
  onActivate?: () => void;
  /** Hands this row's element back, so a flyout can anchor to it. */
  rowRef?: Ref<HTMLDivElement>;
}

function InlineProjectTreeFolderDraftRow({
  level,
  value,
  disabled,
  onChange,
  onSubmit,
  onCancel,
}: {
  level: number;
  value: string;
  disabled: boolean;
  onChange: (value: string) => void;
  onSubmit: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="flex items-center gap-1.5 py-1" style={{ paddingLeft: `${level * 12 + 20}px` }}>
      <FolderOpen className="size-3.5 shrink-0 text-muted-foreground" />
      <Input
        autoFocus
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        placeholder="New folder"
        className="h-7 select-text text-[13px]"
        onBlur={() => {
          if (!disabled) {
            onCancel();
          }
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            onSubmit();
          }
          if (event.key === "Escape") {
            event.preventDefault();
            onCancel();
          }
        }}
      />
    </div>
  );
}

/**
 * Encouragement row rendered under a project-tree-folder-enabled root that has no folders yet.
 * It starts the same inline folder draft as the root's "+" affordance.
 */
function FirstProjectTreeFolderPromptRow({
  level,
  rootLabel,
  onStart,
}: {
  level: number;
  rootLabel: string;
  onStart: () => void;
}) {
  return (
    <button
      type="button"
      data-tree-first-folder-prompt
      className="flex h-7 w-full select-none items-center gap-1.5 pr-2 text-left text-muted-foreground transition-colors hover:bg-muted/50 focus-visible:bg-muted/50 touch:min-h-11"
      style={{ paddingLeft: `${level * 12 + 20}px` }}
      aria-label={`Add your first folder to ${rootLabel}`}
      onClick={(event) => {
        event.stopPropagation();
        onStart();
      }}
    >
      <Plus className="size-[13px] shrink-0" />
      <span className="min-w-0 truncate text-[12.5px] font-medium">Add your first folder</span>
    </button>
  );
}

export function TreeNode({
  item,
  level = 0,
  projectId,
  defaultOpen = true,
  treeExpandState,
  projectTreeFolderDraftParentId,
  projectTreeFolderDraftName,
  isSavingProjectTreeFolder,
  onProjectTreeFolderDraftChange,
  onProjectTreeFolderDraftSubmit,
  onProjectTreeFolderDraftCancel,
  onStartProjectTreeFolderDraft,
  trailing,
  onActivate,
  rowRef,
}: TreeNodeProps) {
  const { isMobile, setOpenMobile } = useSidebar();
  const navigate = useProjectNavigate();
  const hasPersistedChildren = (item.children?.length ?? 0) > 0;
  const hasActiveProjectTreeFolderDraft = projectTreeFolderDraftParentId === item.id;
  const canCreateProjectTreeFolder = item.metadata?.allowProjectTreeFolderCreate === true;
  // The tree is only rendered once project data has loaded (`useProjectTree` returns a null tree
  // while loading and `ProjectSidebar` renders its loading state instead), so an empty child list
  // here means "this root has no folders yet", never "folders have not arrived".
  const shouldShowFirstFolderPrompt =
    canCreateProjectTreeFolder && !hasPersistedChildren && !hasActiveProjectTreeFolderDraft;
  const hasChildren =
    hasPersistedChildren || hasActiveProjectTreeFolderDraft || shouldShowFirstFolderPrompt;
  const entityTypeKey = item.metadata?.entityTypeKey;
  const createEntityFromAction = useCreateEntityFromAction(projectId);
  const createActions = useTreeNodeCreateActions(projectId, item);

  // Check if this node is active by comparing both path and search params
  const isItemActive = useMemo(() => {
    return treeExpandState.activeNodeId === item.id;
  }, [item.id, treeExpandState.activeNodeId]);
  const hasActiveChild = useMemo(() => {
    if (!hasPersistedChildren) return false;
    return item.children!.some((child) =>
      hasActiveDescendant(child, treeExpandState.activeLineageNodeIds)
    );
  }, [hasPersistedChildren, item.children, treeExpandState.activeLineageNodeIds]);

  const computedDefaultOpen = useMemo(() => {
    if (item.type === "root-folder") return true;
    if (hasActiveChild) return true;
    if (item.type === "project-tree-folder") return false;
    // Root-level entity collection folders default to collapsed.
    if (item.type === "entity-collection") {
      return false;
    }
    void entityTypeKey;
    if (level <= 1) return defaultOpen;
    return false;
  }, [item.type, hasActiveChild, entityTypeKey, level, defaultOpen]);

  const isOpen = treeExpandState.isNodeExpanded(item.id, computedDefaultOpen);

  const handleToggle = useCallback(() => {
    treeExpandState.toggleManualNode(item.id, computedDefaultOpen);
  }, [treeExpandState, item.id, computedDefaultOpen]);

  const Icon = item.type === "project-tree-folder" && isOpen ? FolderOpen : item.icon;
  const shouldShowIcon = item.type !== "root-folder";
  const iconColor = getIconColor(item, isItemActive);
  const fontWeight = isItemActive ? "font-semibold" : getFontWeight(item);

  const isFolder = !item.path && hasChildren;

  const handleCreateAction = useCallback(
    async (action: (typeof createActions)[number]) => {
      const rootFolderSlug = item.metadata?.rootFolderSlug;
      if (!rootFolderSlug) {
        return;
      }

      await runGuardedCreateAction(
        projectId,
        action,
        () =>
          createEntityFromAction({
            resolvedAction: action,
            rootFolderSlug,
            returnPath: action.sourcePath ?? item.path ?? `/${rootFolderSlug}`,
            surface: "project_sidebar",
            createdVia: "sidebar_create",
          }),
        (nextPath) => {
          navigate(nextPath);
          if (isMobile) {
            setOpenMobile(false);
          }
        }
      );
    },
    [
      createEntityFromAction,
      isMobile,
      item.metadata?.rootFolderSlug,
      item.path,
      navigate,
      setOpenMobile,
    ]
  );

  const startProjectTreeFolderDraft = useCallback(() => {
    onStartProjectTreeFolderDraft(item);
    if (!isOpen && hasChildren) {
      treeExpandState.expandManualNode(item.id);
    }
  }, [hasChildren, isOpen, item, onStartProjectTreeFolderDraft, treeExpandState]);
  const shouldForceCreateMenu = item.type !== "entity-collection";
  // A folder-organized root whose folder creation lives on its landing page (LAT-1004) shows no
  // create affordance at all: an element "+" here either errors with zero folders or files the
  // element into a folder the user never chose. Collection rows inside folders keep their own
  // "+", which carries real folder context.
  const withholdsRootCreateAffordance =
    !canCreateProjectTreeFolder &&
    item.type === "root-folder" &&
    Boolean(item.metadata?.projectTreeFolderKind);
  const addMenu = canCreateProjectTreeFolder ? (
    <button
      type="button"
      className="ml-auto flex size-7 shrink-0 items-center justify-center rounded-md text-neutral-dim transition-colors hover:bg-muted hover:text-foreground no-touch:absolute no-touch:right-1 no-touch:top-1/2 no-touch:-translate-y-1/2 no-touch:bg-sidebar"
      aria-label={`Create folder under ${item.label}`}
      onMouseDown={(event) => {
        event.preventDefault();
        event.stopPropagation();
        startProjectTreeFolderDraft();
      }}
      onClick={(event) => {
        event.stopPropagation();
        if (event.detail === 0) {
          startProjectTreeFolderDraft();
        }
      }}
    >
      <Plus className="size-4" />
    </button>
  ) : withholdsRootCreateAffordance ? null : createActions.length > 0 ? (
    <CreateEntityButton
      actions={createActions}
      onSelect={handleCreateAction}
      label="Add"
      className="shrink-0 text-neutral-dim transition-opacity no-touch:absolute no-touch:right-1 no-touch:top-1/2 no-touch:-translate-y-1/2 no-touch:bg-sidebar no-touch:opacity-0 no-touch:pointer-events-none no-touch:group-hover/tree-node:opacity-100 no-touch:group-hover/tree-node:pointer-events-auto no-touch:focus-visible:opacity-100 no-touch:focus-visible:pointer-events-auto touch:ml-auto touch:static touch:opacity-100 touch:pointer-events-auto"
      variant="ghost"
      size="icon-sm"
      align="end"
      alwaysMenu={shouldForceCreateMenu}
      iconOnly
      triggerIcon={Plus}
      triggerAriaLabel={`Add to ${item.label}`}
    />
  ) : null;

  const handleClick = useCallback(() => {
    if (onActivate) {
      onActivate();
      return;
    }
    if (item.path) {
      const searchParams = item.metadata?.parentQuestionId
        ? { q: item.metadata.parentQuestionId }
        : undefined;
      const locationKey = getTreeLocationKey(item.path, searchParams);

      treeExpandState.activateNode(item.id, locationKey);
      navigate(item.path, searchParams);
    }
  }, [item.path, item.id, item.metadata, navigate, onActivate, treeExpandState]);

  /**
   * Space on a row that opens something. The tree container's own key handler activates a leaf
   * row on Space only when the row *is* a `<button>` (`use-tree-keyboard.ts`), and these rows are
   * divs — Enter it already handles by clicking `[data-tree-activate]`, so only Space is picked
   * up here and only where an activation exists to run.
   */
  const handleRowKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (!onActivate || event.key !== " ") {
        return;
      }
      event.preventDefault();
      onActivate();
    },
    [onActivate]
  );

  // ARIA treeitem attributes — applied to the outermost element:
  // - Leaf nodes: applied to the content element directly
  // - Parent nodes: applied to the Collapsible wrapper (so role="group" is nested inside)
  const ariaTreeItemProps = {
    role: "treeitem" as const,
    "aria-level": level + 1,
    "aria-selected": isItemActive,
    "data-tree-node-id": item.id,
    tabIndex: -1,
    ...(hasChildren ? { "aria-expanded": isOpen } : {}),
  };
  const label = (
    <LongLabelTooltip
      content={item.label}
      side="right"
      align="start"
      contentClassName="max-w-80 whitespace-normal break-words"
    >
      <span className={cn("min-w-0 truncate text-[13px]", fontWeight)}>{item.label}</span>
    </LongLabelTooltip>
  );

  const nodeContent = (
    // biome-ignore lint/a11y/useKeyWithClickEvents: Keyboard navigation handled at tree container level via useTreeKeyboard
    // biome-ignore lint/a11y/noStaticElementInteractions: For parent nodes, role="treeitem" is on the Collapsible wrapper
    <div
      {...(!hasChildren ? ariaTreeItemProps : {})}
      className={cn(
        "group/tree-node relative flex h-7 select-none items-center gap-1.5 px-2 text-muted-foreground transition-colors touch:min-h-11",
        item.type === "root-folder" ? "h-7" : "",
        isFolder ? "cursor-pointer hover:bg-muted/50 focus-visible:bg-muted/50" : ""
      )}
      style={{ paddingLeft: `${level * 12 + 8}px` }}
      onClick={isFolder ? handleToggle : undefined}
    >
      {hasChildren ? (
        <button
          type="button"
          tabIndex={-1}
          data-tree-toggle
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleToggle();
          }}
          className="flex items-center justify-center w-3.5 shrink-0 text-neutral-dim"
        >
          {isOpen ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
        </button>
      ) : (
        <span className="w-3.5 shrink-0" />
      )}
      {shouldShowIcon ? <Icon className={cn("size-3.5 shrink-0", iconColor)} /> : null}
      {label}
      {addMenu}
    </div>
  );

  const linkClasses = cn(
    "group/tree-node relative flex h-7 w-full cursor-pointer select-none items-center gap-1.5 border-l-2 border-transparent px-2 text-left text-muted-foreground transition-colors hover:bg-muted/50 focus-visible:bg-muted/50 touch:min-h-11",
    isItemActive && "bg-muted border-primary text-foreground",
    item.type === "root-folder" ? "h-7" : ""
  );

  const linkInnerContent = (
    <>
      {hasChildren ? (
        <button
          type="button"
          tabIndex={-1}
          data-tree-toggle
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleToggle();
          }}
          className="flex items-center justify-center w-3.5 shrink-0 text-neutral-dim"
        >
          {isOpen ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
        </button>
      ) : (
        <span className="w-3.5 shrink-0" />
      )}
      {shouldShowIcon ? <Icon className={cn("size-3.5 shrink-0", iconColor)} /> : null}
      {label}
      {addMenu}
      {trailing ? (
        <span className="ml-auto flex shrink-0 items-center gap-1.5 pl-1">{trailing}</span>
      ) : null}
    </>
  );

  const linkContent = item.path ? (
    hasChildren ? (
      // biome-ignore lint/a11y/noStaticElementInteractions: Parent treeitem semantics are on the Collapsible wrapper
      <div
        ref={rowRef}
        data-tree-activate
        onClick={handleClick}
        onKeyDown={handleRowKeyDown}
        className={linkClasses}
        style={{ paddingLeft: `${level * 12 + 8}px` }}
      >
        {linkInnerContent}
      </div>
    ) : (
      // biome-ignore lint/a11y/noStaticElementInteractions: Tree semantics are provided by ariaTreeItemProps
      <div
        ref={rowRef}
        data-tree-activate
        {...ariaTreeItemProps}
        onClick={handleClick}
        onKeyDown={handleRowKeyDown}
        className={linkClasses}
        style={{ paddingLeft: `${level * 12 + 8}px` }}
      >
        {linkInnerContent}
      </div>
    )
  ) : null;

  const content = item.path ? linkContent : nodeContent;

  if (!hasChildren) {
    return content;
  }

  return (
    <Collapsible open={isOpen} onOpenChange={handleToggle} {...ariaTreeItemProps}>
      {content}
      <CollapsibleContent role="group">
        {item.children?.map((child) => (
          <TreeNode
            key={child.id}
            item={child}
            level={level + 1}
            projectId={projectId}
            defaultOpen={level < 1}
            treeExpandState={treeExpandState}
            projectTreeFolderDraftParentId={projectTreeFolderDraftParentId}
            projectTreeFolderDraftName={projectTreeFolderDraftName}
            isSavingProjectTreeFolder={isSavingProjectTreeFolder}
            onProjectTreeFolderDraftChange={onProjectTreeFolderDraftChange}
            onProjectTreeFolderDraftSubmit={onProjectTreeFolderDraftSubmit}
            onProjectTreeFolderDraftCancel={onProjectTreeFolderDraftCancel}
            onStartProjectTreeFolderDraft={onStartProjectTreeFolderDraft}
          />
        ))}
        {shouldShowFirstFolderPrompt ? (
          <FirstProjectTreeFolderPromptRow
            level={level + 1}
            rootLabel={item.label}
            onStart={startProjectTreeFolderDraft}
          />
        ) : null}
        {hasActiveProjectTreeFolderDraft ? (
          <InlineProjectTreeFolderDraftRow
            level={level + 1}
            value={projectTreeFolderDraftName}
            disabled={isSavingProjectTreeFolder}
            onChange={onProjectTreeFolderDraftChange}
            onSubmit={onProjectTreeFolderDraftSubmit}
            onCancel={onProjectTreeFolderDraftCancel}
          />
        ) : null}
      </CollapsibleContent>
    </Collapsible>
  );
}
